// Cálculo dos valores de uma marcação.
//
// Modelo de parcelas (migração 060): o que foi efetivamente pago vive na tabela
// `pagamentos` (uma parcela `sinal` e uma `restante` por marcação, cada uma com
// valor/método/estado/faturação). O `total` continua congelado na marcação
// (preco_total). Esta função combina os dois:
//
//   total    = preço congelado (preco_total) ou, em falta, o preço atual do serviço
//   sinal    = valor da parcela de sinal, se paga (0 se não paga / inexistente)
//   restante = valor da parcela de restante, se paga; senão o que falta (total − sinal)
//   recebido   = sinal pago + restante pago
//   porReceber = total − recebido
//
// Compatibilidade: `pagamentos` é OPCIONAL. Sem parcelas, cai no comportamento
// legado (lê sinal_pago/sinal_valor/restante_pago da própria marcação) para os
// ecrãs ainda não migrados (Relatórios, Página Início). Os ecrãs novos (Detalhe,
// Cobrar) passam as parcelas e obtêm o estado real.
export function valoresAgendamento(ag, pagamentos) {
  const total = ag?.preco_total != null
    ? Number(ag.preco_total)
    : (ag?.servicos?.preco != null ? Number(ag.servicos.preco) : 0)

  const parcelas = Array.isArray(pagamentos)
    ? pagamentos.filter(p => p && p.agendamento_id === ag?.id)
    : null

  let sinal, restante, sinalPago, restantePago
  if (parcelas) {
    const pSinal = parcelas.find(p => p.tipo === 'sinal')
    const pRest = parcelas.find(p => p.tipo === 'restante')
    sinalPago = !!(pSinal && pSinal.pago)
    restantePago = !!(pRest && pRest.pago)
    sinal = sinalPago ? Number(pSinal.valor || 0) : 0
    restante = restantePago
      ? Number(pRest.valor || 0)
      : Math.max(total - sinal, 0)
  } else {
    // Legado (marcação sem parcelas carregadas).
    sinalPago = !!ag?.sinal_pago
    restantePago = !!ag?.restante_pago
    sinal = sinalPago ? Number(ag?.sinal_valor || 0) : 0
    restante = Math.max(total - sinal, 0)
  }

  const recebido = (sinalPago ? sinal : 0) + (restantePago ? restante : 0)
  const porReceber = Math.max(total - recebido, 0)
  return { total, sinal, restante, recebido, porReceber, sinalPago, restantePago }
}

// Estado das parcelas de uma marcação, para os ecrãs de cobrança/detalhe. Devolve
// { sinal, restante } com { valor, metodo, pago, faturado, origem, existe } por
// parcela. `sinalEsperado`/`restanteEsperado` são os valores sugeridos quando a
// parcela ainda não existe (para pré-preencher os campos editáveis).
export function estadoParcelas(ag, pagamentos, { sinalEsperado = 0 } = {}) {
  const total = ag?.preco_total != null
    ? Number(ag.preco_total)
    : (ag?.servicos?.preco != null ? Number(ag.servicos.preco) : 0)
  const lista = Array.isArray(pagamentos)
    ? pagamentos.filter(p => p && p.agendamento_id === ag?.id)
    : []

  const parcela = (tipo, esperado) => {
    const p = lista.find(x => x.tipo === tipo)
    if (!p) {
      return { existe: false, pago: false, faturado: false, valor: esperado, valorSugerido: esperado, metodo: null, origem: 'salao' }
    }
    const valor = Number(p.valor || 0)
    // Numa parcela por pagar sem valor definido (placeholder), sugerimos o valor
    // esperado em vez de 0 — senão a página de cobrança mostraria 0 € a cobrar.
    const valorSugerido = (!p.pago && valor === 0) ? esperado : valor
    return {
      existe: true,
      pago: !!p.pago,
      faturado: !!p.fatura_id,
      valor,
      valorSugerido,
      metodo: p.metodo || null,
      origem: p.origem || 'salao',
      id: p.id,
      fatura_id: p.fatura_id || null,
      fatura_url: p.fatura_url || null,
    }
  }

  const sinal = parcela('sinal', Number(sinalEsperado) || 0)
  // Restante esperado = total − sinal (pago ou esperado).
  const baseSinal = sinal.pago ? sinal.valor : (Number(sinalEsperado) || 0)
  const restante = parcela('restante', Math.max(total - baseSinal, 0))
  return { sinal, restante }
}

// Que parcelas de uma marcação PODEM ser cobradas: todas as que não estão
// pagas. Inclui o restante de uma marcação futura — o cliente está no salão e
// pode querer deixar tudo pago de uma vez, incluindo o da próxima ida.
export function parcelasPorCobrar({ sinal, restante }) {
  const tipos = []
  if (sinal && !sinal.pago) tipos.push('sinal')
  if (restante && !restante.pago) tipos.push('restante')
  return tipos
}

// Que parcelas estão PAGAS MAS POR FATURAR — a base do modo "emitir fatura".
// Inclui o sinal pago por Stripe: é dinheiro recebido e precisa de documento
// como qualquer outro. (Durante muito tempo o Stripe foi excluído da emissão,
// o que deixava esses sinais sem fatura nenhuma.)
export function parcelasPorFaturar({ sinal, restante }) {
  const tipos = []
  if (sinal && sinal.pago && !sinal.faturado) tipos.push('sinal')
  if (restante && restante.pago && !restante.faturado) tipos.push('restante')
  return tipos
}

// Que parcelas se SUGEREM (vêm pré-escolhidas). Diferente do que é possível:
// numa marcação futura o restante fica de fora, porque o valor ainda pode
// mudar (serviços acrescentados, duração real). Continua disponível para quem
// o quiser marcar à mão.
export function parcelasSugeridas({ futuro, sinal, restante }) {
  return parcelasPorCobrar({ sinal, restante })
    .filter(t => !(t === 'restante' && futuro))
}

export const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`

// Métodos de pagamento do valor restante (liquidado no salão). Lista de
// fallback — usada quando o tenant ainda não configurou nada em Config Admin
// ("Tipos de pagamento") ou como seed inicial nesse ecrã.
export const METODOS_PAGAMENTO_DEFAULT = [
  { chave: 'dinheiro',      label: 'Dinheiro' },
  { chave: 'multibanco',    label: 'Multibanco' },
  { chave: 'mbway',         label: 'MB WAY' },
  { chave: 'transferencia', label: 'Transferência' },
  { chave: 'outro',         label: 'Outro' },
]

// Chave reservada: identifica a parcela do sinal paga via Stripe na
// associação com o Vendus (não é um método escolhível no salão).
export const METODO_STRIPE = 'stripe'

// Deriva a lista de métodos de pagamento a partir das configurações do
// tenant (chave `metodos_pagamento`, JSON). Cai para a lista por defeito
// quando ainda não há configuração guardada ou o JSON é inválido/vazio.
export function parseMetodosPagamento(cfg) {
  try {
    const arr = JSON.parse(cfg?.metodos_pagamento || 'null')
    if (Array.isArray(arr) && arr.length > 0) {
      const validos = arr.filter(m => m && typeof m.chave === 'string' && m.chave && typeof m.label === 'string' && m.label)
      if (validos.length > 0) return validos
    }
  } catch { /* JSON inválido — usa o defeito */ }
  return METODOS_PAGAMENTO_DEFAULT
}

// Gera uma chave estável a partir do rótulo (minúsculas, sem acentos,
// separado por "_"). Usada só na criação — depois de criado o método a
// chave não muda, mesmo que o rótulo seja editado (fica gravada em
// pagamentos.metodo).
export function slugifyMetodo(label) {
  return String(label || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

// Rótulo de um método. O sinal pago via Stripe (online) não é um método
// configurável, por isso tem rótulo próprio; os restantes vêm da lista do
// tenant (ou da lista por defeito).
export const labelMetodo = (chave, metodos = METODOS_PAGAMENTO_DEFAULT) =>
  chave === METODO_STRIPE
    ? 'Stripe (online)'
    : (metodos.find(m => m.chave === chave)?.label || chave || '—')
